import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  LinearProgress,
  Chip,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Paper,
} from '@mui/material';
import {
  NavigateNext,
  NavigateBefore,
  Save,
  CheckCircle,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { auditAPI, AuditQuestion, type AuditSession } from '../services/api';
import SubtaskGenerationModal from '../components/SubtaskGenerationModal';
import ReactMarkdown from 'react-markdown';

const AuditSessionPage: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user } = useAuth();
  const { selectedCompany } = useCompany();

  const [session, setSession] = useState<AuditSession | null>(null);
  const [questions, setQuestions] = useState<AuditQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  
  // Subtask generation modal state
  const [showSubtaskModal, setShowSubtaskModal] = useState(false);
  const [subtaskTaskId, setSubtaskTaskId] = useState<string | null>(null);

  useEffect(() => {
    if (sessionId) {
      loadSessionData();
      checkForActiveTask();
    }
  }, [sessionId]);

  // Load existing answer when question changes
  useEffect(() => {
    if (session && questions.length > 0 && currentQuestionIndex >= 0) {
      const currentQuestion = questions[currentQuestionIndex];
      const existingAnswer = session.answers?.find(
        answer => answer.question_header === currentQuestion.id
      );
      
      if (existingAnswer) {
        setSelectedAnswer(existingAnswer.question_detail);
      } else {
        setSelectedAnswer(null);
      }
    }
  }, [currentQuestionIndex, session, questions]);

  // Check if there's an active subtask generation task (for when user returns)
  const checkForActiveTask = useCallback(async () => {
    try {
      const response = await auditAPI.getActiveSubtaskTask(parseInt(sessionId!));
      if (response.active_task && !response.active_task.is_complete) {
        // There's an active task in progress - show the modal
        setSubtaskTaskId(response.active_task.task_id);
        setShowSubtaskModal(true);
      }
    } catch (error) {
      // Silently ignore - task might not exist
      console.log('No active subtask task found');
    }
  }, [sessionId]);

  const loadSessionData = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Load session details
      const sessionData = await auditAPI.getAuditSession(parseInt(sessionId!));
      setSession(sessionData);

      // Load questions for this audit series
      const questionsData = await auditAPI.getAuditQuestions(sessionData.audit_series);
      setQuestions(questionsData);

      // Find the last answered question to resume from
      if (sessionData.answers && sessionData.answers.length > 0) {
        const lastAnsweredQuestionId = sessionData.answers[sessionData.answers.length - 1].question_header;
        const lastAnsweredIndex = questionsData.findIndex((q: AuditQuestion) => q.id === lastAnsweredQuestionId);
        if (lastAnsweredIndex !== -1 && lastAnsweredIndex < questionsData.length - 1) {
          setCurrentQuestionIndex(lastAnsweredIndex + 1);
        }
      }
    } catch (error: any) {
      console.error('Error loading session data:', error);
      setError('Failed to load review session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnswerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedAnswer(parseInt(event.target.value));
  };

  const handleNext = async () => {
    if (selectedAnswer === null) {
      setError('Please select an answer before continuing.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');

      // Submit the current answer
      await auditAPI.submitAnswer({
        session_id: parseInt(sessionId!),
        question_header_id: questions[currentQuestionIndex].id,
        question_detail_id: selectedAnswer,
      });

      // Reload session data to get updated answers
      const updatedSession = await auditAPI.getAuditSession(parseInt(sessionId!));
      setSession(updatedSession);

      // Move to next question or complete
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        // selectedAnswer will be set by useEffect based on existing answer
        setSuccessMessage('Answer saved successfully!');
        setTimeout(() => setSuccessMessage(''), 2000);
      } else {
        // Complete the audit - this now returns a task_id for background subtask generation
        const response = await auditAPI.completeAudit(parseInt(sessionId!));
        
        // If there are action items, show the subtask generation modal
        if (response.subtask_generation?.task_id) {
          setSubtaskTaskId(response.subtask_generation.task_id);
          setShowSubtaskModal(true);
        } else {
          // No action items - navigate directly to analysis
          navigate(`/audit-analysis/${sessionId}`);
        }
      }
    } catch (error: any) {
      console.error('Error submitting answer:', error);
      setError('Failed to save answer. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      // selectedAnswer will be set by useEffect based on existing answer
    }
  };

  const handleSaveProgress = async () => {
    if (selectedAnswer === null) {
      setError('Please select an answer before saving.');
      return;
    }

    try {
      setIsSaving(true);
      setError('');

      await auditAPI.saveProgress({
        session_id: parseInt(sessionId!),
        answers: [{
          question_header_id: questions[currentQuestionIndex].id,
          question_detail_id: selectedAnswer,
        }],
      });

      // Reload session data to get updated answers
      const updatedSession = await auditAPI.getAuditSession(parseInt(sessionId!));
      setSession(updatedSession);

      setSuccessMessage('Progress saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      console.error('Error saving progress:', error);
      setError('Failed to save progress. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const getProgressPercentage = () => {
    return ((currentQuestionIndex + 1) / questions.length) * 100;
  };

  const getCurrentQuestion = () => {
    return questions[currentQuestionIndex];
  };

  const getQuestionHierarchy = (question: AuditQuestion) => {
    const levels = [question.level01, question.level02, question.level03, question.level04, question.level05];
    return levels.filter(level => level && level.trim() !== '');
  };



  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!session || questions.length === 0) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">
          Review session not found or no questions available.
        </Alert>
      </Container>
    );
  }

  const currentQuestion = getCurrentQuestion();

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate('/audit-selection');
            }}
          >
            Audit Selection
          </Link>
          <Typography color="text.primary">Session #{sessionId}</Typography>
        </Breadcrumbs>

        <Typography variant="h4" component="h1" gutterBottom>
          Review Session
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {selectedCompany?.name} 
          {session?.site_name && ` • ${session.site_name}`}
          {' • '}Question {currentQuestionIndex + 1} of {questions.length}
        </Typography>
      </Box>

      {/* Progress Bar */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="body2" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {currentQuestionIndex + 1} / {questions.length}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={getProgressPercentage()}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </CardContent>
      </Card>

      {/* Error Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Question Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          {/* Question Hierarchy */}
          {getQuestionHierarchy(currentQuestion).length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Category
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {getQuestionHierarchy(currentQuestion).map((level, index) => (
                  <Chip
                    key={index}
                    label={level}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Question Description */}
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            {currentQuestion.description}
          </Typography>

          {/* Question Details */}
          {currentQuestion.long_text && (
            <Paper sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
              <Typography
                variant="body1"
                component="div"
                sx={{
                  color: 'grey.900',
                  '& p': { mb: 1, lineHeight: 1.6 },
                  '& ul, & ol': { pl: 2, mb: 1 },
                  '& li': { mb: 0.25 },
                  '& strong': { fontWeight: 600 },
                  '& code': {
                    fontFamily: 'monospace',
                    bgcolor: 'action.hover',
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 0.5,
                  },
                  '& pre': {
                    bgcolor: 'action.hover',
                    p: 1.5,
                    borderRadius: 1,
                    overflow: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    '& code': {
                      bgcolor: 'transparent',
                      p: 0,
                    },
                  },
                  '& a': { color: 'primary.main', textDecoration: 'underline' },
                }}
              >
                <ReactMarkdown>
                  {currentQuestion.long_text}
                </ReactMarkdown>
              </Typography>
            </Paper>
          )}

          {/* Answer Options */}
          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Select your answer:
            </Typography>
            <RadioGroup
              value={selectedAnswer || ''}
              onChange={handleAnswerChange}
            >
              {currentQuestion.options?.map((option) => (
                <FormControlLabel
                  key={option.id}
                  value={option.id}
                  control={<Radio />}
                  label={
                    <Typography variant="body1">
                      {option.option_text}
                    </Typography>
                  }
                  sx={{
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    p: 2,
                    mb: 1,
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover',
                    },
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<NavigateBefore />}
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
        >
          Previous
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Save />}
            onClick={handleSaveProgress}
            disabled={selectedAnswer === null || isSaving}
          >
            {isSaving ? <CircularProgress size={20} /> : 'Save Progress'}
          </Button>

          <Button
            variant="contained"
            endIcon={currentQuestionIndex === questions.length - 1 ? <CheckCircle /> : <NavigateNext />}
            onClick={handleNext}
            disabled={selectedAnswer === null || isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={20} />
            ) : currentQuestionIndex === questions.length - 1 ? (
              'Complete Review'
            ) : (
              'Next Question'
            )}
          </Button>
        </Box>
      </Box>

      {/* Success Message - Fixed position below buttons */}
      <Box sx={{ height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {successMessage && (
          <Alert 
            severity="success" 
            sx={{ 
              width: 'fit-content',
              animation: 'fadeIn 0.3s ease-in-out'
            }}
          >
            {successMessage}
          </Alert>
        )}
      </Box>

      {/* Session Info */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Session Information
          </Typography>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {session.site_name && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Site
                </Typography>
                <Typography variant="body1">
                  {session.site_name}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography variant="body2" color="text.secondary">
                Started
              </Typography>
              <Typography variant="body1">
                {new Date(session.start_date).toLocaleString()}
              </Typography>
            </Box>
            {session.end_date && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Completed
                </Typography>
                <Typography variant="body1">
                  {new Date(session.end_date).toLocaleString()}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography variant="body2" color="text.secondary">
                Status
              </Typography>
              <Chip
                label={session.end_date ? 'Completed' : 'In Progress'}
                color={session.end_date ? 'success' : 'warning'}
                size="small"
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Subtask Generation Modal */}
      <SubtaskGenerationModal
        open={showSubtaskModal}
        taskId={subtaskTaskId}
        onClose={() => {
          setShowSubtaskModal(false);
          // Navigate to analysis page when modal is closed
          navigate(`/audit-analysis/${sessionId}`);
        }}
        onComplete={() => {
          // Navigate to analysis page on completion
          navigate(`/audit-analysis/${sessionId}`);
        }}
      />
    </Container>
  );
};

export default AuditSessionPage; 